// GSBS Centralized Authentication Service
// Integrates with auth.gsbooks.org for Google OAuth authentication

import AUTH_CONFIG from '../config/auth';

class AuthService {
  constructor() {
    this.authBaseUrl = AUTH_CONFIG.AUTH_SERVICE_URL;
  }

  async checkAuth() {
    try {
      const response = await fetch(`${this.authBaseUrl}/auth/status`, {
        method: 'GET',
        credentials: 'include', // CRITICAL: includes JWT cookie
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Auth check failed: ${response.status}`);
      }
      
      const auth = await response.json();
      return auth.authenticated ? 
        { isAuthenticated: true, user: auth.user, expires: auth.expires } : 
        { isAuthenticated: false };
    } catch (error) {
      console.error('Auth check failed:', error);
      return { isAuthenticated: false };
    }
  }

  redirectToAuth(returnUrl = window.location.href) {
    const encodedReturn = encodeURIComponent(returnUrl);
    window.location.href = `${this.authBaseUrl}/auth/google?redirect=${encodedReturn}`;
  }

  async logout(redirectUrl = window.location.origin) {
    try {
      const encodedRedirect = encodeURIComponent(redirectUrl);
      
      // Use fetch for API-style logout (returns JSON)
      const response = await fetch(`${this.authBaseUrl}/logout?redirect=${encodedRedirect}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json', // Triggers JSON response
          'X-Requested-With': 'XMLHttpRequest' // Identifies as API request
        }
      });
      
      if (response.ok) {
        window.location.reload();
      } else {
        throw new Error('Logout failed');
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Fallback: redirect to logout URL
      window.location.href = `${this.authBaseUrl}/logout?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  }

  // Silent auth check without redirecting
  async silentAuthCheck() {
    try {
      const response = await fetch(`${this.authBaseUrl}/auth/status`, {
        credentials: 'include'
      });
      return await response.json();
    } catch (error) {
      return { authenticated: false };
    }
  }

  // Pre-flight auth check before making API calls
  async authenticatedRequest(url, options = {}) {
    const auth = await this.silentAuthCheck();
    
    if (!auth.authenticated) {
      throw new Error('Authentication required');
    }
    
    return fetch(url, {
      ...options,
      credentials: 'include'
    });
  }
}

export default AuthService; 